import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  IssueTypeColor,
  UpdateIssueIssueTypeInput,
  updateIssueIssueTypeSharedMutation,
  updateIssueIssueTypeSharedMutation$data,
  updateIssueIssueTypeSharedMutation$rawResponse,
} from './__generated__/updateIssueIssueTypeSharedMutation.graphql'

// eslint-disable-next-line relay/no-future-added-value
type IssueFieldDataType = 'DATE' | 'NUMBER' | 'SINGLE_SELECT' | 'TEXT' | '%future added value'

type BaseIssueField = {
  readonly dataType: IssueFieldDataType
  readonly id: string
  readonly name: string
}

type OptimisticPinnedField =
  | (BaseIssueField & {readonly __typename: 'IssueFieldDate'})
  | (BaseIssueField & {readonly __typename: 'IssueFieldNumber'})
  | (BaseIssueField & {readonly __typename: 'IssueFieldSingleSelect'})
  | (BaseIssueField & {readonly __typename: 'IssueFieldText'})
  | {readonly __typename: '%other'}

export type OptimisticIssueType = {
  id: string
  name: string
  description: string | null | undefined
  color: IssueTypeColor
  isEnabled?: boolean
  pinnedFields?: readonly OptimisticPinnedField[] | null
}

/**
 * Builds an optimistic response for the updateIssueIssueType mutation.
 * This allows the UI to update immediately without waiting for the server response.
 */
export function buildOptimisticResponse(
  issueId: string,
  issueType: OptimisticIssueType | null,
): updateIssueIssueTypeSharedMutation$rawResponse {
  return {
    updateIssueIssueType: {
      issue: {
        id: issueId,
        issueType: issueType
          ? {
              id: issueType.id,
              name: issueType.name,
              description: issueType.description ?? null,
              color: issueType.color,
              isEnabled: issueType.isEnabled ?? true,
              pinnedFields: (issueType.pinnedFields ?? []).map(field => {
                if (field.__typename === '%other') {
                  // For unknown types, we need __isNode and id for the raw response
                  return {__typename: '%other' as const, __isNode: '%other' as const, id: ''}
                }
                return {
                  __typename: field.__typename,
                  __isNode: field.__typename,
                  id: field.id,
                  name: field.name,
                  dataType: field.dataType,
                }
              }),
            }
          : null,
      },
    },
  }
}

export function commitUpdateIssueIssueTypeMutation({
  environment,
  input,
  optimisticIssueType,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: UpdateIssueIssueTypeInput
  /** The full issue type object to use for optimistic updates. If provided, the UI will update immediately. */
  optimisticIssueType?: OptimisticIssueType | null
  onError?: (error: Error) => void
  onCompleted?: (response: updateIssueIssueTypeSharedMutation$data) => void
}) {
  return commitMutation<updateIssueIssueTypeSharedMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateIssueIssueTypeSharedMutation($input: UpdateIssueIssueTypeInput!) @raw_response_type {
        updateIssueIssueType(input: $input) {
          issue {
            issueType {
              id
              name
              isEnabled
              description
              color
              pinnedFields {
                __typename
                ... on IssueFieldText {
                  id
                  name
                  dataType
                }
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                }
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                }
                ... on IssueFieldNumber {
                  id
                  name
                  dataType
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticResponse:
      optimisticIssueType !== undefined ? buildOptimisticResponse(input.issueId, optimisticIssueType) : undefined,
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}
